// Iter146 — Target-driven basket optimizer modal (Mini-Spec Part 2).
// Iter G (iter148) — + Commit-to-trade-log button + Explain-my-basket
// Markdown export. Reuses the explainability payload from iter147.
// Renders the proposed basket + risk benchmark strip + Sizing Coach chip.
// User chooses Accept & Select All / Commit / Adjust / Cancel — never auto-writes.
import React from "react";
import api from "@/lib/api";

function _fmtAge(sec) {
  if (sec == null) return "just now";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.round(sec / 3600)}h`;
}

function _basketToMarkdown(s) {
  if (!s) return "";
  const e = s.explainability || {};
  const rb = s.risk_budget || null;
  const lines = [];
  lines.push(`# Basket suggestion — target $${s.target_premium?.toLocaleString?.() || s.target_premium}`);
  lines.push("");
  lines.push(`- Total premium: **$${Math.round(s.total_premium).toLocaleString()}** (${e.target_pct_reached ?? "?"}% of target)`);
  if (rb) {
    lines.push(`- **Risk-Budget model (Iter149):**`);
    lines.push(`  - Excess Liquidity: $${Math.round(rb.excess_liquidity).toLocaleString()}`);
    lines.push(`  - Utilization: ${Math.round(rb.utilization_factor * 100)}% → Risk Budget: $${Math.round(rb.risk_budget).toLocaleString()}`);
    lines.push(`  - Existing options stressed usage: $${Math.round(rb.existing_options_stressed_usage).toLocaleString()}`);
    lines.push(`  - Available for new trades: **$${Math.round(rb.available_for_new_trades).toLocaleString()}**`);
    lines.push(`  - New stressed margin: **$${Math.round(rb.stressed_new_margin).toLocaleString()}** (${rb.budget_used_pct}% of available, ${rb.stress_multiplier}× haircut)`);
    lines.push(`  - Account data freshness: ${rb.staleness_state} (${_fmtAge(rb.staleness_age_sec)} ago, source=${rb.source})`);
    if (s.cc_share_coverage?.enforced) {
      lines.push(`  - CC positions do not consume this budget (share-covered)`);
    }
  } else {
    lines.push(`- Capital deployed: $${Math.round(s.capital_deployed).toLocaleString()} (${s.capital_pct_of_netliq}% of NetLiq)`);
  }
  lines.push(`- Weekly RoR: ${s.weekly_ror_pct}%/wk · Annualized: ${Math.round(s.annualized_ror_pct)}%/yr`);
  if (s.worst_per_trade) {
    lines.push(`- Worst per-trade: ${s.worst_per_trade.pct}% (${s.worst_per_trade.ticker}) — legacy per-trade limit ${s.worst_per_trade.limit}%`);
  }
  lines.push(`- Source: ${s.from_cache ? `from cache (${Math.round(s.cache_age_sec)}s old)` : "fresh compute"}`);
  lines.push("");
  lines.push("## How it was built");
  lines.push(`- Stage 1 (regime-aligned only): **$${Math.round(e.stage1_premium || 0).toLocaleString()}** in ${e.stage1_count || 0} row(s)`);
  if (e.stage2_used) {
    lines.push(`- Stage 2 (relaxed): **+$${Math.round(e.stage2_premium || 0).toLocaleString()}**`);
    if (e.stage2_reason) lines.push(`  - Why Stage 2: ${e.stage2_reason}`);
  } else {
    lines.push(`- Stage 2: not needed`);
  }
  if (!e.hit_target && e.near_miss) {
    lines.push(`- Near miss: ${e.near_miss.message}`);
  }
  if (s.sizing_coach) {
    lines.push(`- ⚡ Sizing Coach: ${s.sizing_coach.reason}`);
  }
  if (s.concentration) {
    lines.push(`- Concentration caps: ≤${s.concentration.max_contracts_per_ticker} contracts/ticker · ≤${s.concentration.max_ticker_basket_pct}% of target per ticker${s.concentration.max_sector_concentration_pct != null ? ` · ≤${s.concentration.max_sector_concentration_pct}% per sector` : ""}`);
    (s.concentration.capped_rows || []).forEach(r => lines.push(`  - ${r.ticker} capped at ${r.contracts}x (${r.capped_by})`));
    (s.concentration.sector_blocked || []).forEach(b => lines.push(`  - ${b.reason}`));
  }
  if (s.earnings_filter?.enabled && s.earnings_filter.excluded_count > 0) {
    lines.push(`- Earnings filter (${s.earnings_filter.mode}): ${s.earnings_filter.excluded_count} excluded`);
    (s.earnings_filter.excluded || []).forEach(x => lines.push(`  - ${x.reason}`));
  }
  lines.push("");
  lines.push("## Basket");
  lines.push("| Ticker | Strategy | Strikes | Cts | Premium | Capital | Stressed | Max Risk | Y%/wk | Fit | Stage |");
  lines.push("|---|---|---|---|---|---|---|---|---|---|---|");
  (s.basket || []).forEach(r => {
    lines.push(`| ${r.ticker} | ${r.strategy} | ${r.strikes_label || "—"} | ${r.contracts}x | $${Math.round(r.premium)} | $${Math.round(r.capital_req).toLocaleString()} | ${r.share_covered ? "share-cov" : "$" + Math.round(r.stressed_margin || 0).toLocaleString()} | $${Math.round(r.max_loss).toLocaleString()} | ${r.yield_pct_per_week}% | ${r.regime_fit || "—"} | S${r.basket_stage} |`);
  });
  return lines.join("\n");
}

export function SuggestBasketModal({ open, suggestion, loading, onAccept, onAdjust, onCancel, onRightSize }) {
  const [committing, setCommitting] = React.useState(false);
  const [committed, setCommitted] = React.useState(null);
  const [exportOpen, setExportOpen] = React.useState(false);
  const s = suggestion;

  const handleRightSize = () => {
    if (onRightSize) onRightSize();
  };

  const handleCommit = async () => {
    if (!s?.basket?.length) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/income-engine/commit-basket-to-trade-log", {
        basket: s.basket,
        notes: `Auto-committed from Suggest Basket (target $${s.target_premium})`,
      });
      setCommitted(data);
      // Iter183a — trade-log mutation → force the pinned LED to refresh.
      window.dispatchEvent(new Event("ml-risk-health-refresh"));
    } catch (e) {
      console.warn("Commit basket failed:", e);
      setCommitted({ error: e?.response?.data?.detail || "Commit failed" });
    } finally { setCommitting(false); }
  };

  const handleExport = () => {
    const md = _basketToMarkdown(s);
    // Copy to clipboard AND open a small preview panel so user can grab it.
    if (navigator.clipboard) {
      navigator.clipboard.writeText(md).catch(() => {});
    }
    setExportOpen(true);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="suggest-basket-modal">
      <div className="w-full max-w-4xl max-h-[85vh] overflow-y-auto rounded-md border shadow-lg"
        style={{ background: "var(--hoip-bg)", borderColor: "var(--hoip-border)" }}>

        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between"
          style={{ borderColor: "var(--hoip-border)" }}>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold" style={{ color: "var(--hoip-text-primary)" }}>
              Basket screen results — filtered to your ${s?.target_premium?.toLocaleString?.() || "—"} target
            </h2>
            {s?.from_cache ? (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-sm"
                style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA", border: "1px solid rgba(59,130,246,0.3)" }}
                title={`Cached suggestion from ${s.cache_age_sec}s ago`}
                data-testid="basket-cache-label">
                From cache · {Math.round(s.cache_age_sec)}s
              </span>
            ) : (s && (
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-sm"
                style={{ background: "rgba(16,185,129,0.12)", color: "#34D399", border: "1px solid rgba(16,185,129,0.3)" }}
                data-testid="basket-fresh-label">
                Fresh
              </span>
            ))}
          </div>
          <button onClick={onCancel} className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="basket-cancel-x">✕</button>
        </div>

        {loading && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--hoip-text-tertiary)" }}>
            Computing basket… (up to 100 candidates evaluated)
          </div>
        )}

        {!loading && s && (
          <>
            {/* Summary strip */}
            <div className="px-4 py-3 border-b grid grid-cols-4 gap-3 text-[11px]"
              style={{ borderColor: "var(--hoip-border)" }}>
              <div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Total premium</div>
                <div className="font-mono-data font-bold text-emerald-500">${Math.round(s.total_premium).toLocaleString()}</div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  {s.shortfall > 0 ? `${Math.round((s.total_premium / s.target_premium) * 100)}% of target — $${Math.round(s.shortfall)} short` : "target hit"}
                </div>
              </div>
              <div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  {s.risk_budget ? "New risk (stressed)" : "Capital deployed"}
                </div>
                {s.risk_budget ? (
                  <>
                    <div className={`font-mono-data font-bold ${s.risk_budget.budget_used_pct >= 100 ? "text-red-400" : s.risk_budget.budget_used_pct >= 75 ? "text-amber-500" : "text-emerald-500"}`}
                      data-testid="basket-risk-budget-pct"
                      title={`Stressed by ${s.risk_budget.stress_multiplier}× multiplier`}>
                      ${Math.round(s.risk_budget.stressed_new_margin).toLocaleString()} · {s.risk_budget.budget_used_pct}% of budget
                    </div>
                    <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Legacy: ${Math.round(s.capital_deployed).toLocaleString()} unstressed
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`font-mono-data font-bold ${s.capital_pct_of_netliq >= 50 ? "text-red-400" : s.capital_pct_of_netliq >= 30 ? "text-amber-500" : "text-emerald-500"}`}
                      data-testid="basket-capital-pct">
                      {s.capital_pct_of_netliq}% of NetLiq
                    </div>
                    <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>${Math.round(s.capital_deployed).toLocaleString()}</div>
                  </>
                )}
              </div>
              <div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Weekly RoR</div>
                <div className="font-mono-data font-bold" style={{ color: "var(--hoip-text-primary)" }}>{s.weekly_ror_pct}%/wk</div>
                <div className="text-[9px] flex items-center gap-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                  ≈ {Math.round(s.annualized_ror_pct)}%/yr
                  {/* Iter177 §C.5 — SEC Marketing Rule requires disclosure
                      when displaying targeted/projected performance. Tooltip
                      + inline caret makes the "hypothetical extrapolation"
                      framing visible right next to the annualized figure. */}
                  <span title="Annualized figure is a mathematical extrapolation of the current weekly premium target and does not account for compounding risk, assignment, drawdown, or the likelihood of repeating this result. Not a projection of actual expected return."
                    style={{ cursor: "help", color: "var(--hoip-amber)" }}
                    data-testid="annualized-return-disclaimer">†</span>
                </div>
              </div>
              <div>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Worst per-trade</div>
                {s.worst_per_trade ? (
                  <>
                    <div className={`font-mono-data font-bold ${s.worst_per_trade.pct > s.worst_per_trade.limit ? "text-red-400" : "text-emerald-500"}`}>
                      {s.worst_per_trade.pct}% ({s.worst_per_trade.ticker})
                    </div>
                    <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>limit {s.worst_per_trade.limit}%</div>
                  </>
                ) : <div className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>—</div>}
              </div>
            </div>

            {/* Iter178 §C.4 — remediation-locked disclaimer wording. Frames
                the modal output as a rules-based filter over user-set
                Risk Limits, with a direct link back to the Settings panel
                so users see the exact rules driving what they see. */}
            <div className="mx-4 mt-3 px-3 py-2 border rounded-sm text-[10px] leading-relaxed"
              style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-nav)", color: "var(--hoip-text-tertiary)" }}
              data-testid="basket-rules-filter-disclaimer">
              <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>
                Rules-based filter output:
              </span>{" "}
              The following combinations of open, liquid contracts satisfy the
              target and Risk Limits you configured in{" "}
              <a href="/settings" className="underline"
                style={{ color: "var(--hoip-text-secondary)" }}
                data-testid="basket-risk-limits-settings-link">
                Settings
              </a>
              . This is not investment advice or a recommendation to enter any
              trade. Selecting, sizing, and placing trades is entirely your
              decision.
            </div>
            {/* Iter186a — Portfolio Risk Health + earnings-exclusion blocks
                removed from this modal (redundant: PRH lives in the pinned
                LED/panel; exclusions are the filter's silent work). */}
            {/* Iter182 — Concentration caps strip. */}
            {s.concentration && (
              <div className="mx-4 mt-3 px-3 py-2 border rounded-sm text-[10px] leading-relaxed"
                style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-nav)", color: "var(--hoip-text-tertiary)" }}
                data-testid="basket-concentration-rules">
                <span className="font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Concentration caps: </span>
                ≤{s.concentration.max_contracts_per_ticker} contract{s.concentration.max_contracts_per_ticker !== 1 ? "s" : ""}/ticker
                {" · "}≤{s.concentration.max_ticker_basket_pct}% of target per ticker
                {s.concentration.max_sector_concentration_pct != null && (
                  <> · ≤{s.concentration.max_sector_concentration_pct}% per sector</>
                )}
                {(s.concentration.capped_rows || []).length > 0 && (
                  <span className="ml-1" data-testid="basket-concentration-capped">
                    — capped: {s.concentration.capped_rows.map(r => `${r.ticker} @ ${r.contracts}x (${r.capped_by === "max_contracts_per_ticker" ? "contract cap" : r.capped_by === "max_ticker_basket_pct" ? "% cap" : "sector cap"})`).join(", ")}
                  </span>
                )}
                {(s.concentration.sector_blocked || []).length > 0 && (
                  <span className="ml-1 text-amber-500" data-testid="basket-sector-blocked"
                    title={s.concentration.sector_blocked.map(b => b.reason).join("\n")}>
                    — {s.concentration.sector_blocked.length} row{s.concentration.sector_blocked.length !== 1 ? "s" : ""} skipped by sector cap
                  </span>
                )}
              </div>
            )}
            {s.risk_budget && (
              <div className="mx-4 mt-3 p-2.5 rounded-sm border text-[11px]"
                style={{
                  background: s.risk_budget.staleness_state === "stale" ? "rgba(245,158,11,0.08)" : "rgba(139,92,246,0.06)",
                  borderColor: s.risk_budget.staleness_state === "stale" ? "rgba(245,158,11,0.35)" : "rgba(139,92,246,0.25)",
                  color: "var(--hoip-text-primary)",
                }}
                data-testid="basket-risk-budget-block">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="font-bold" style={{ color: s.risk_budget.staleness_state === "stale" ? "#F59E0B" : "#A78BFA" }}>
                    Risk budget · Excess-Liquidity model
                  </div>
                  <span className="text-[9px] font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}
                    data-testid="basket-risk-freshness"
                    title={s.risk_budget.last_updated ? `Data captured ${new Date(s.risk_budget.last_updated).toLocaleTimeString()}` : "No timestamp"}>
                    Account data {s.risk_budget.staleness_age_sec != null ? `as of ${_fmtAge(s.risk_budget.staleness_age_sec)} ago` : "just now"}
                    {s.risk_budget.staleness_state === "stale" && (
                      <span className="ml-1 px-1 rounded-sm" style={{ background: "rgba(245,158,11,0.2)", color: "#F59E0B" }}>⚠ stale</span>
                    )}
                    {s.risk_budget.staleness_state === "aging" && (
                      <span className="ml-1" style={{ color: "#F59E0B" }}>· aging</span>
                    )}
                  </span>
                </div>
                <div className="text-[10px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
                  Excess Liquidity: <b>${Math.round(s.risk_budget.excess_liquidity).toLocaleString()}</b>
                  <span style={{ color: "var(--hoip-text-tertiary)" }}> → Risk Budget ({Math.round(s.risk_budget.utilization_factor * 100)}% util): <b>${Math.round(s.risk_budget.risk_budget).toLocaleString()}</b></span>
                  <span style={{ color: "var(--hoip-text-tertiary)" }}> → After existing positions (${Math.round(s.risk_budget.existing_options_stressed_usage).toLocaleString()} stressed): <b>${Math.round(s.risk_budget.available_for_new_trades).toLocaleString()}</b> available</span>
                </div>
                <div className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                  New risk: <span className={`font-mono-data font-bold ${s.risk_budget.budget_used_pct >= 100 ? "text-red-400" : s.risk_budget.budget_used_pct >= 75 ? "text-amber-500" : "text-emerald-500"}`}>
                    ${Math.round(s.risk_budget.stressed_new_margin).toLocaleString()} stressed margin ({s.risk_budget.budget_used_pct}% of available)
                  </span>
                  {s.cc_share_coverage?.enforced && (
                    <span className="ml-2" style={{ color: "var(--hoip-text-tertiary)" }}>
                      · CC positions do not consume this budget (share-covered)
                    </span>
                  )}
                </div>
                {s.risk_budget.source === "seeded" && (
                  <div className="text-[9px] mt-1 px-1.5 py-0.5 rounded-sm inline-block"
                    style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}
                    data-testid="basket-seeded-warning">
                    Note: no positions on file — account data is seeded ($100k default). Numbers are illustrative.
                  </div>
                )}
              </div>
            )}

            {/* Sizing Coach chip */}
            {s.sizing_coach && (
              <div className="mx-4 mt-3 p-2.5 rounded-sm border text-[11px]"
                style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.3)", color: "var(--hoip-text-primary)" }}
                data-testid="sizing-coach-chip">
                <div className="font-bold text-amber-500 mb-1">⚡ Sizing Coach</div>
                <div>{s.sizing_coach.reason}. {s.sizing_coach.unlock_row_count} more rows would fit at <b>{s.sizing_coach.suggested_profile}</b> profile.</div>
                <div className="text-[9px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
                  Accept this basket first, then switch profile separately if you want to unlock more.
                </div>
              </div>
            )}

            {/* Iter147 Explainability strip — Stage 1 vs Stage 2 breakdown */}
            {s.explainability && (
              <div className="mx-4 mt-3 p-2.5 rounded-sm border text-[11px]"
                style={{ background: "rgba(59,130,246,0.06)", borderColor: "rgba(59,130,246,0.25)", color: "var(--hoip-text-primary)" }}
                data-testid="basket-explainability">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="font-bold" style={{ color: "#60A5FA" }}>How this basket was built</div>
                  <span className="text-[9px] px-1 py-0.5 rounded-sm"
                    style={{ background: "rgba(148,163,184,0.12)", color: "var(--hoip-text-tertiary)" }}>
                    {s.explainability.target_pct_reached}% of target
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Stage 1 · regime-aligned only
                    </div>
                    <div className="font-mono-data font-bold text-emerald-500" data-testid="stage1-premium">
                      ${Math.round(s.explainability.stage1_premium).toLocaleString()} <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>({s.explainability.stage1_count} row{s.explainability.stage1_count !== 1 ? "s" : ""})</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Stage 2 · relaxation
                    </div>
                    {s.explainability.stage2_used ? (
                      <div className="font-mono-data font-bold text-amber-500" data-testid="stage2-premium">
                        +${Math.round(s.explainability.stage2_premium).toLocaleString()}
                      </div>
                    ) : (
                      <div className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="stage2-not-used">
                        not needed
                      </div>
                    )}
                  </div>
                </div>
                {s.explainability.stage2_reason && (
                  <div className="mt-2 pt-2 border-t text-[10px]"
                    style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-secondary)" }}
                    data-testid="basket-stage2-reason">
                    <span className="font-bold text-amber-500">Why Stage 2: </span>
                    {s.explainability.stage2_reason}
                  </div>
                )}
                {!s.explainability.hit_target && s.explainability.near_miss && (
                  <div className="mt-2 pt-2 border-t text-[10px]"
                    style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-secondary)" }}
                    data-testid="basket-near-miss">
                    <span className="font-bold" style={{ color: "#F87171" }}>Near miss: </span>
                    {s.explainability.near_miss.message}
                  </div>
                )}
              </div>
            )}

            {/* Basket table */}
            <div className="px-4 py-3 overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    <th className="text-left py-1" style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</th>
                    <th className="text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Strategy</th>
                    <th className="text-left" style={{ color: "var(--hoip-text-tertiary)" }}>Strikes</th>
                    <th className="text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Cts</th>
                    <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }}>Premium</th>
                    <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }}>Capital</th>
                    <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }}>Max Risk</th>
                    <th className="text-right" style={{ color: "var(--hoip-text-tertiary)" }} title="Weekly RoR">Y%/wk</th>
                    <th className="text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Fit</th>
                    <th className="text-center" style={{ color: "var(--hoip-text-tertiary)" }}>Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {(s.basket || []).map((r, i) => (
                    <tr key={`${r.ticker}-${i}`} className="border-b" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`basket-row-${r.ticker}`}>
                      <td className="py-1.5 font-bold" style={{ color: "var(--hoip-text-primary)" }}>{r.ticker}</td>
                      <td className="text-center">{r.strategy}</td>
                      <td>{r.strikes_label}</td>
                      <td className="text-center font-bold">{r.contracts}x</td>
                      <td className="text-right font-mono-data text-emerald-500">${Math.round(r.premium)}</td>
                      <td className="text-right font-mono-data">${Math.round(r.capital_req).toLocaleString()}</td>
                      <td className="text-right font-mono-data text-red-400">${Math.round(r.max_loss).toLocaleString()}</td>
                      <td className="text-right font-mono-data">{r.yield_pct_per_week}%</td>
                      <td className="text-center">
                        <span className={`text-[9px] font-bold px-1 rounded-sm ${r.regime_fit === "aligns" ? "bg-emerald-500/10 text-emerald-400" : r.regime_fit === "against" ? "bg-red-500/10 text-red-400" : "bg-zinc-500/10 text-zinc-400"}`}>
                          {r.regime_fit || "—"}
                        </span>
                      </td>
                      <td className="text-center text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>S{r.basket_stage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!s.basket || s.basket.length === 0) && (
                <div className="text-center py-4 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>
                  No candidates fit within your sizing profile for this target.
                </div>
              )}
            </div>

            {/* Iter G: Commit success / error strip */}
            {committed && (
              <div className="mx-4 mt-3 p-2 rounded-sm border text-[11px] flex items-center justify-between"
                style={committed.error
                  ? { background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.35)", color: "#F87171" }
                  : { background: "rgba(16,185,129,0.08)", borderColor: "rgba(16,185,129,0.35)", color: "#34D399" }}
                data-testid="basket-commit-status">
                {committed.error ? (
                  <span>Commit failed: {committed.error}</span>
                ) : (
                  <>
                    <span>
                      ✓ Committed {committed.count} row{committed.count !== 1 ? "s" : ""} to Trade Log
                      (batch <code className="font-mono-data">{committed.batch_id}</code>, status=<b>planned</b>).
                    </span>
                    <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                      Review in Trade Log → change status to `open` when executed.
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Iter G: Explain-my-basket Markdown export preview */}
            {exportOpen && (
              <div className="mx-4 mt-3 rounded-sm border text-[11px]"
                style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
                data-testid="basket-export-preview">
                <div className="px-2 py-1.5 border-b flex items-center justify-between"
                  style={{ borderColor: "var(--hoip-border)" }}>
                  <div className="font-bold" style={{ color: "var(--hoip-text-primary)" }}>
                    Markdown copied to clipboard
                  </div>
                  <button onClick={() => setExportOpen(false)}
                    className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}
                    data-testid="basket-export-close">✕</button>
                </div>
                <pre className="p-2 max-h-40 overflow-auto text-[9px] font-mono-data"
                  style={{ color: "var(--hoip-text-secondary)", whiteSpace: "pre-wrap" }}
                  data-testid="basket-export-markdown">
                  {_basketToMarkdown(s)}
                </pre>
              </div>
            )}

            {/* Action buttons */}
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2"
              style={{ borderColor: "var(--hoip-border)" }}>
              <button onClick={onCancel}
                className="h-7 px-4 text-[11px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                data-testid="basket-cancel">Cancel</button>
              <button onClick={handleExport}
                disabled={!s.basket || s.basket.length === 0}
                className="h-7 px-3 text-[11px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                title="Copy basket + explainability as Markdown to your clipboard"
                data-testid="basket-export">Explain my basket</button>
              <button onClick={onAdjust}
                className="h-7 px-4 text-[11px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
                style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                data-testid="basket-adjust">Adjust in table</button>
              {s.risk_budget && s.risk_budget.available_for_new_trades > 0 && (
                <button onClick={handleRightSize}
                  className="h-7 px-3 text-[11px] font-medium rounded-sm border transition-colors hover:bg-[var(--hoip-hover)]"
                  style={{ borderColor: "rgba(139,92,246,0.4)", color: "#A78BFA" }}
                  title="Rebuild the basket capped at 90% of available Excess-Liquidity budget for a 10% safety cushion"
                  data-testid="basket-right-size">Right-size to budget</button>
              )}
              <button onClick={handleCommit}
                disabled={!s.basket || s.basket.length === 0 || committing || committed?.status === "ok"}
                className="h-7 px-4 text-[11px] font-medium rounded-sm border-0 transition-colors bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                title="Insert this basket into Trade Log as status='planned' rows"
                data-testid="basket-commit">
                {committing ? "Logging…" : (committed?.status === "ok" ? "Logged ✓" : "Log Selected Trades")}
              </button>
              <button onClick={onAccept}
                disabled={!s.basket || s.basket.length === 0}
                className="h-7 px-4 text-[11px] font-bold rounded-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="basket-accept">Add All to Selection</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
